import { useState, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import background from "../../../assets/gi/wave_background.svg";
import data from "./CharacterSelectionData.json";
import { useNavigate, useLocation } from "react-router-dom";
import avatarsImg from "../../../assets/gi/Avatars";
import { postAnalytics } from "../../../../PatientComponents/analytics";
import Transition from "./Transition";
import { buttonCSS, desktopCss } from "./general.style";
import { usePatientFacing } from "../../../flow/patientFacing/hooks/usePatientFacing";
import axios from "axios";
import GIPatientHeader from "./components/GIPatientHeader";

export default function GICharacterSelection() {
  const { t } = useTranslation();
  const { getEventDataLocal } = usePatientFacing();
  const event = getEventDataLocal();
  const caseId = event?.Case?.id;

  const navigate = useNavigate();
  const location = useLocation();
  const isGi2 = location.pathname.includes('/gi2/');
  const basePath = isGi2 ? '../gi2' : '../gi';
  const videoPath = isGi2 ? `${basePath}/video1` : `${basePath}/video`;
  const questionnairePath = `${basePath}/Questionnaire/diabetesMedicines`;

  // Skip avatar selection if already done
  const cpData = event?.Case?.CasesProgresses;
  const cpArray = Array.isArray(cpData) ? cpData : (cpData ? [cpData] : []);
  const giProgress = cpArray.find(cp => cp?.facingType === 'gi');
  const hasAvatar = !!giProgress?.avatarSelection;
  const hasQuestionnaire = !!giProgress?.answeredQuestionnaire;

  useEffect(() => {
    if (hasAvatar && hasQuestionnaire) {
      navigate(videoPath, { replace: true });
    } else if (hasAvatar) {
      navigate(questionnairePath, { replace: true });
    }
  }, [hasAvatar, hasQuestionnaire, navigate, videoPath, questionnairePath]);

  const [answers, setAnswers] = useState({});
  const [avatarKey, setAvatarKey] = useState("");
  const [avatar, setAvatar] = useState({});
  const [showError, setShowError] = useState(false);

  const answerQuestion = (questionKey, answerKey) => () => {
    postAnalytics(caseId, `answer-${questionKey}-${answerKey}`);
    setAvatarKey("");
    setAnswers((prev) => ({ ...prev, [questionKey]: answerKey }));
  };

  const handleAvatar = (key, avatar) => () => {
    postAnalytics(caseId, `avatarKey-${key}`);
    const missinsAnswers = !Object.keys(data).every((key) => key in answers);
    setShowError(missinsAnswers);
    if (missinsAnswers) return;
    setAvatarKey(key);
    setAvatar(avatar);
  };

  const filtersAvatars = Object.values(answers).reduce(
    (prev, answer) => prev.filter(({ fields }) => fields.includes(answer)),
    avatars
  );

  // Map avatar ethnicity to backend expected values
  const mapEthnicity = (ethnicity) => {
    const mapping = {
      white: "light",
      black: "dark"
    };
    return mapping[ethnicity] || ethnicity;
  };

  // Map age answer to backend expected values
  const mapAge = (ageAnswer) => {
    const mapping = {
      "20-50": "young",
      "50-70": "middle",
      "70+": "old"
    };
    return mapping[ageAnswer] || ageAnswer;
  };

  const handelNext = async () => {
    if (!avatarKey) return setShowError(true);

    // Get selected values with proper mapping
    const selectedAge = mapAge(answers.age);
    const selectedGender = avatar.gender;
    const selectedEthnicity = mapEthnicity(avatar.ethnicity);
    const selectedLanguage = "english";

    console.log("Avatar payload:", { selectedAge, selectedGender, selectedEthnicity, answers, avatar });

    const avatarPayload = {
      caseId,
      gender: selectedGender,
      age: selectedAge,
      ethnicity: selectedEthnicity,
      language: selectedLanguage
    };

    // Update sessionStorage immediately so GIVideo reads the correct avatar
    const stored = sessionStorage.getItem("eventData");
    if (stored) {
      try {
        const eventData = JSON.parse(stored);
        eventData.Case = eventData.Case || {};
        eventData.Case.Avatar = {
          gender: selectedGender,
          age: selectedAge,
          ethnicity: selectedEthnicity,
        };
        sessionStorage.setItem("eventData", JSON.stringify(eventData));
      } catch (e) {
        console.error("Failed to update sessionStorage avatar:", e);
      }
    }

    // Save avatar and record progress before navigating so the panel stays in sync
    const analyticsPromise = postAnalytics(caseId, "general-information-answered");
    try {
      await axios.post("/api/avatar", avatarPayload);
    } catch (err) {
      console.error("Failed to save avatar:", err);
    }
    try {
      await analyticsPromise;
    } catch (err) {
      console.error("Failed to record avatar analytics:", err);
    }

    navigate(questionnairePath);
  };

  return (
    <Transition>
      <Page>
        <Desktop>
          <GIPatientHeader padding="8px 5px" />
          <Title>
            {t("Character-Selection-Title")}
          </Title>
          <PickerContainer id="PickerContainer">
            {Object.entries(data).map(([questionKey, questionData]) => (
              <Fragment key={questionKey}>
                <Question id="Question">
                  {t(questionData.title)}
                </Question>
                <Options>
                  {Object.entries(questionData.answers).map(
                    ([answerKey, answerText]) => (
                      <Fragment key={answerKey}>
                        <Option
                          selected={answerKey === answers[questionKey]}
                          onClick={answerQuestion(questionKey, answerKey)}
                        >
                          {t(answerText)}
                        </Option>
                        <Divider />
                      </Fragment>
                    )
                  )}
                </Options>
                <Line />
              </Fragment>
            ))}
            <Question id="Question">
              {t(`Character-Selection-Avatar-${filtersAvatars.length === 1 ? "Single" : "General"}`)}
            </Question>
            <AvatarOptions>
              {filtersAvatars.map(({ key, avatar, image }) => (
                <Avatar
                  key={key}
                  selected={key === avatarKey}
                  onClick={handleAvatar(key, avatar)}
                  src={image}
                />
              ))}
            </AvatarOptions>
            <Error id="Error" show={showError}>
              {t("Character-Selection-Error")}
            </Error>
          </PickerContainer>
          <ConfirmationButton
            id="Button"
            onClick={handelNext}
            avatarKey={avatarKey}
          >
            {t("Next")}
          </ConfirmationButton>
        </Desktop>
      </Page>
    </Transition>
  );
}

const avatars = [
  {
    key: "male_young_white",
    fields: ["male", "20-50", "50-70", "other"],
    avatar: { gender: "male", age: "young", ethnicity: "white" },
    image: avatarsImg.male_young_white,
  },
  {
    key: "female_young_white",
    fields: ["female", "20-50", "50-70", "other"],
    avatar: { gender: "female", age: "young", ethnicity: "white" },
    image: avatarsImg.female_young_white,
  },
  {
    key: "male_young_black",
    fields: ["male", "20-50", "50-70", "other"],
    avatar: { gender: "male", age: "young", ethnicity: "black" },
    image: avatarsImg.male_young_black,
  },
  {
    key: "female_young_black",
    fields: ["female", "20-50", "50-70", "other"],
    avatar: { gender: "female", age: "young", ethnicity: "black" },
    image: avatarsImg.female_young_black,
  },
  {
    key: "male_middle_white",
    fields: ["male", "50-70", "70+", "other"],
    avatar: { gender: "male", age: "middle", ethnicity: "white" },
    image: avatarsImg.male_middle_white,
  },
  {
    key: "female_middle_white",
    fields: ["female", "50-70", "70+", "other"],
    avatar: { gender: "female", age: "middle", ethnicity: "white" },
    image: avatarsImg.female_middle_white,
  },
  {
    key: "male_middle_black",
    fields: ["male", "50-70", "70+", "other"],
    avatar: { gender: "male", age: "middle", ethnicity: "black" },
    image: avatarsImg.male_middle_black,
  },
  {
    key: "female_middle_black",
    fields: ["female", "50-70", "70+", "other"],
    avatar: { gender: "female", age: "middle", ethnicity: "black" },
    image: avatarsImg.female_middle_black,
  },
  {
    key: "male_old_white",
    fields: ["male", "70+", "other"],
    avatar: { gender: "male", age: "old", ethnicity: "white" },
    image: avatarsImg.male_old_white,
  },
  {
    key: "female_old_white",
    fields: ["female", "70+", "other"],
    avatar: { gender: "female", age: "old", ethnicity: "white" },
    image: avatarsImg.female_old_white,
  },
  {
    key: "male_old_black",
    fields: ["male", "70+", "other"],
    avatar: { gender: "male", age: "old", ethnicity: "black" },
    image: avatarsImg.male_old_black,
  },

  {
    key: "female_old_black",
    fields: ["female", "70+", "other"],
    avatar: { gender: "female", age: "old", ethnicity: "black" },
    image: avatarsImg.female_old_black,
  },
];

const Page = styled.div`
  min-height: calc(100dvh - var(--header-size));
  background-image: url("${background}");
  background-repeat: no-repeat;
  background-size: cover;
  padding-inline: 17px;
  box-sizing: border-box;
  --inner-padding-inline: 18px;
  font-family: "Abraham", sans-serif;
  font-weight: 400;
`;

const Desktop = styled.div`
  ${desktopCss}
`;

const Title = styled.p`
  text-align: start;
  font-family: "Abraham", sans-serif;
  font-size: 1.19rem;
  font-weight: 600;
  padding-inline: var(--inner-padding-inline);
  padding-block: 1.75rem 1.75rem;
  margin: 0px;
`;

const PickerContainer = styled.div`
  display: grid;
  grid-template-columns:
    [full-start]
    var(--inner-padding-inline)
    [content-start] 1fr [content-end]
    var(--inner-padding-inline)
    [full-end];
  background-color: #ffffff;
  border-radius: 15px;
  padding-block: 1.75rem 1.875rem;
  box-shadow: 0px 3px 10px 0px #0000000d;
  row-gap: 12px;
`;

const Question = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1.1875rem;
  text-align: start;
  margin: 0rem;
  grid-column: content;
`;

const Options = styled.div`
  display: flex;
  overflow-x: auto;
  grid-column: content;
  cursor: pointer;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (width > 700px) {
    flex-wrap: wrap;
  }
`;

const AvatarOptions = styled(Options)`
  --avatar-border-width: 2px;
  grid-column: full;
  gap: calc(17px - (var(--avatar-border-width) * 2));
  padding-inline: calc(
    var(--inner-padding-inline) - var(--avatar-border-width)
  );
`;

const Option = styled.div`
  color: ${({ selected }) => (selected ? "#84A4FC" : "#A7A7A7")};
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1.1875rem;
  white-space: nowrap;
`;

const Avatar = styled.img`
  border-radius: 50%;
  border: var(--avatar-border-width) solid
    ${({ selected }) => (selected ? "#84a4fc" : "transparent")};
  width: 4.688rem;
  height: 4.688rem;
`;

const Divider = styled.div`
  margin: 0 1rem;
  width: 1px;
  background-color: #eaeaea;
  border-radius: 10px;
  :last-child {
    display: none;
  }
`;

const Line = styled.div`
  height: 1px;
  width: 100%;
  background: #eaeaea;
  grid-column: content;
  margin-block: 6px;
`;

const ConfirmationButton = styled.button`
  ${buttonCSS}
  font-size: 1.063rem;
  padding: 0.5rem 3rem;
  opacity: ${({ avatarKey }) => (avatarKey ? 1 : 0.6)};
  margin-top: 2.125rem;
  display: block;
  margin-inline: auto;
`;

const Error = styled.p`
  grid-column: content;
  color: #f02a4c;
  font-size: 0.875rem;
  margin: 0rem;
  display: ${({ show }) => (show ? "block" : "none")};
  ::before {
    content: "*";
  }
`;
